import { useEffect, useRef, useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { AlertTriangle, CheckCircle2, Copy, ExternalLink, RotateCw } from 'lucide-react';
import { previewCaPdf, previewCaEmail, requestCa } from '@/services/deals.service.js';
import { cn } from '@/lib/utils.js';
import { toast } from '@/components/ui/toaster.jsx';
import PdfPreviewFrame from './PdfPreviewFrame.jsx';
import { formatDateOrEmpty } from '@/lib/date-display.js';

export default function CaRequestDialog({ dealId, open, onOpenChange, onChange }) {
  const [recipient, setRecipient] = useState(null);
  const [subject, setSubject] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [emailHtml, setEmailHtml] = useState(null);
  const [tab, setTab] = useState('pdf');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const pdfUrlRef = useRef(null);

  async function loadPreview() {
    if (!dealId) return;
    setLoading(true); setError(null);
    try {
      const [pdfResult, emailResult] = await Promise.allSettled([
        previewCaPdf(dealId),
        previewCaEmail(dealId),
      ]);
      if (emailResult.status === 'fulfilled') {
        const emailRes = emailResult.value;
        setEmailHtml(emailRes?.html || null);
        setRecipient(emailRes?.recipient || null);
        setSubject(emailRes?.subject || '');
      }
      if (pdfResult.status === 'fulfilled') {
        const newUrl = URL.createObjectURL(pdfResult.value);
        const prev = pdfUrlRef.current;
        pdfUrlRef.current = newUrl;
        setPdfUrl(newUrl);
        if (prev) URL.revokeObjectURL(prev);
      }
      const failures = [];
      if (pdfResult.status === 'rejected') {
        failures.push(`CA PDF preview did not load (${pdfResult.reason?.message || 'unknown'}).`);
      }
      if (emailResult.status === 'rejected') {
        failures.push(`Email preview did not load (${emailResult.reason?.message || 'unknown'}).`);
      }
      if (failures.length) {
        const sendable = emailResult.status === 'fulfilled' && emailResult.value?.recipient?.toEmail;
        setError(sendable
          ? `${failures.join(' ')} You can still click Send.`
          : failures.join(' '));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open && dealId && !done) loadPreview();
  }, [open, dealId]);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setRecipient(null); setSubject(''); setPdfUrl(null);
        setEmailHtml(null); setTab('pdf'); setError(null); setDone(null);
        if (pdfUrlRef.current) { URL.revokeObjectURL(pdfUrlRef.current); pdfUrlRef.current = null; }
      }, 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => () => {
    if (pdfUrlRef.current) { URL.revokeObjectURL(pdfUrlRef.current); pdfUrlRef.current = null; }
  }, []);

  async function handleSend() {
    if (!dealId || sending) return;
    setSending(true); setError(null);
    try {
      const res = await requestCa(dealId);
      if (!res?.success) throw new Error(res?.error || 'Send failed');
      setDone(res);
      for (const w of res.warnings || []) toast.info(w);
      onChange?.();
    } catch (e) {
      setError(e?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  }

  function copyLink() {
    if (!done?.link || typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard.writeText(done.link).catch(() => {});
  }

  const canSend = !sending && !loading && !!recipient?.toEmail;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="flex h-[96dvh] flex-col overflow-hidden p-0 md:mx-auto md:max-w-5xl"
      >
        {/* Set height, not max-h: the flex-1 preview below only fills a
            parent that has one. Same fix as BrRequestDialog (2026-09-30). */}
        <SheetHeader className="px-4 pb-3 pr-14 pt-4">
          <SheetTitle>Commercial aviation binding signature request</SheetTitle>
          <SheetDescription>
            Review what the insured will receive. Nothing is sent until you click Send.
          </SheetDescription>
        </SheetHeader>

        <div className="border-y border-border bg-surface px-4 py-2 text-sm">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">To</span>
          {recipient ? (
            <span className="ml-2 text-text">
              {recipient.toName ? <>{recipient.toName} &lt;{recipient.toEmail}&gt;</> : recipient.toEmail}
            </span>
          ) : (
            <span className="ml-2 text-text-muted italic">{loading ? 'Loading...' : 'No recipient resolved'}</span>
          )}
          {subject && <div className="mt-0.5"><span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">Subject</span><span className="ml-2 text-text text-xs">{subject}</span></div>}
        </div>

        {done ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6">
            <CheckCircle2 size={48} className="text-green-600" />
            <p className="text-center text-sm text-text">
              Signing link sent{done.emailed_to ? <> to <strong>{done.emailed_to}</strong></> : ''}.
              {done.expires_at && <> Expires {formatDateOrEmpty(done.expires_at, { month: 'short', day: 'numeric', year: 'numeric' })}.</>}
            </p>
            {done.link && (
              <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-strong px-3 py-2 text-xs">
                <span className="max-w-[340px] truncate text-text-muted">{done.link}</span>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={copyLink} title="Copy link">
                  <Copy size={12} />
                </Button>
                <Button asChild size="icon" variant="ghost" className="h-6 w-6" title="Open link">
                  <a href={done.link} target="_blank" rel="noopener noreferrer"><ExternalLink size={12} /></a>
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex border-b border-border px-4">
              <button
                onClick={() => setTab('pdf')}
                className={cn('px-3 py-2 text-xs font-medium border-b-2 -mb-px transition',
                  tab === 'pdf' ? 'border-accent text-accent' : 'border-transparent text-text-muted hover:text-text')}
              >Application PDF</button>
              <button
                onClick={() => setTab('email')}
                className={cn('px-3 py-2 text-xs font-medium border-b-2 -mb-px transition',
                  tab === 'email' ? 'border-accent text-accent' : 'border-transparent text-text-muted hover:text-text')}
              >Email preview</button>
            </div>

            <div className="flex-1 overflow-hidden">
              {loading ? (
                <div className="flex h-full items-center justify-center"><p className="text-sm text-text-muted">Loading previews...</p></div>
              ) : (
                <>
                  {tab === 'pdf' && (pdfUrl
                    ? <PdfPreviewFrame url={pdfUrl} title="CA application PDF preview" openLabel="Open application PDF" />
                    : <div className="flex h-full items-center justify-center"><p className="text-sm text-text-muted">PDF preview unavailable</p></div>
                  )}
                  {tab === 'email' && (emailHtml
                    ? <iframe srcDoc={emailHtml} className="h-full w-full border-0" title="Email preview" sandbox="allow-same-origin" />
                    : <div className="flex h-full items-center justify-center"><p className="text-sm text-text-muted">Email preview unavailable</p></div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {error && (
          <div className="flex items-start gap-2 border-t border-border bg-amber-50 px-4 py-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-amber-600" />
            <p className="text-xs text-amber-800">{error}</p>
          </div>
        )}

        <SheetFooter className="border-t border-border px-4 py-3">
          {done ? (
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          ) : (
            <div className="flex w-full items-center justify-between">
              <Button variant="ghost" size="sm" onClick={loadPreview} disabled={loading}>
                <RotateCw size={14} className={cn('mr-1', loading && 'animate-spin')} />
                Regenerate
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button onClick={handleSend} disabled={!canSend}>
                  {sending ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </div>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
